import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { format } from "date-fns";
import { ArrowLeft, Phone, MessageSquare, Mail, MapPin, Cake, Sparkles, Package, Gem, TrendingUp, Plus, Edit3, X, MessageCircle, Download } from "lucide-react";
import { WhatsAppWishDialog } from "@/components/WhatsAppWishDialog";
import { WhatsAppShareDialog } from "@/components/WhatsAppShareDialog";
import { useAuth } from "@/context/AuthContext";
import { downloadVCard } from "@/lib/contactExport";

export default function ContactDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, company } = useAuth();
  const canMerge = ["admin", "super-admin", "manager"].includes(user?.role);
  const [c, setC] = useState(null);
  const [interactionOpen, setInteractionOpen] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ type: "call", summary: "", follow_up_date: "" });
  const [dealOpen, setDealOpen] = useState(false);
  const [dealForm, setDealForm] = useState({ title: "", stage: "new", estimated_value: 0, product_category: "", expected_close_date: "" });
  const [editing, setEditing] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  /** Build a scan-parsed-style shape from the current contact so we can reuse
   *  the vCard + WhatsApp helpers. */
  const asParsed = () => ({
    name: c?.name || "",
    company: c?.company_name || "",
    designation: c?.designation || "",
    phones: [c?.mobile, c?.whatsapp_number].filter(Boolean),
    emails: [c?.email].filter(Boolean),
    websites: [],
    address: [c?.address, c?.city, c?.state].filter(Boolean).join(", "),
  });

  const load = async () => {
    const { data } = await api.get(`/contacts/${id}`);
    setC(data);
  };
  useEffect(() => { load(); }, [id]);

  const addInteraction = async () => {
    if (!interactionForm.summary.trim()) return toast.error("Summary required");
    try {
      await api.post("/interactions", {
        contact_id: id, ...interactionForm,
        follow_up_date: interactionForm.follow_up_date ? new Date(interactionForm.follow_up_date).toISOString() : null,
      });
      toast.success("Interaction logged");
      setInteractionOpen(false); setInteractionForm({ type: "call", summary: "", follow_up_date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addDeal = async () => {
    if (!dealForm.title.trim()) return toast.error("Title required");
    try {
      await api.post("/deals", {
        contact_id: id, ...dealForm,
        estimated_value: Number(dealForm.estimated_value) || 0,
        expected_close_date: dealForm.expected_close_date ? new Date(dealForm.expected_close_date).toISOString() : null,
      });
      toast.success("Deal created");
      setDealOpen(false); setDealForm({ title: "", stage: "new", estimated_value: 0, product_category: "", expected_close_date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const savePrefs = async (patch) => {
    try {
      await api.patch(`/contacts/${id}`, patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!c) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="contact-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Contact</div>
          <h1 className="font-serif text-4xl font-medium mt-1" data-testid="contact-name">{c.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[#4A5A50]">
            {c.mobile && <span className="flex items-center gap-1"><Phone size={14}/>{c.mobile}</span>}
            {c.whatsapp_number && <span className="flex items-center gap-1"><MessageSquare size={14}/>{c.whatsapp_number}</span>}
            {c.email && <span className="flex items-center gap-1"><Mail size={14}/>{c.email}</span>}
            {c.city && <span className="flex items-center gap-1"><MapPin size={14}/>{c.city}, {c.state}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            <StatusPill status={c.contact_type}/>
            {(c.tags || []).map((t,i) => <span key={i} className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">{t}</span>)}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => downloadVCard(asParsed())}
            className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"
            data-testid="contact-save-phone-btn"
          ><Download size={14} className="mr-1"/> Save to phone</Button>
          <Button
            variant="outline"
            onClick={() => setWaOpen(true)}
            className="rounded-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
            data-testid="contact-whatsapp-btn"
          ><MessageCircle size={14} className="mr-1"/> WhatsApp</Button>
          <Button variant="outline" onClick={() => setWishOpen(true)} className="rounded-full border-[#25D366] text-[#1B8B4B] hover:bg-[#25D366]/10" data-testid="send-wish-btn"><MessageCircle size={14} className="mr-1"/> Send Wish</Button>
          <Button variant="outline" onClick={() => setInteractionOpen(true)} className="rounded-full" data-testid="log-interaction-btn"><MessageSquare size={14} className="mr-1"/> Log Interaction</Button>
          <Button variant="outline" onClick={() => setDealOpen(true)} className="rounded-full" data-testid="add-deal-btn"><TrendingUp size={14} className="mr-1"/> Add Deal</Button>
          {canMerge && (
            <Button
              variant="outline"
              onClick={() => setMergeOpen(true)}
              className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="merge-contact-btn"
            ><Plus size={14} className="mr-1"/> Merge duplicate</Button>
          )}
          <Button onClick={() => nav(`/orders/new?client_id=${id}`)} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-order-for-contact"><Plus size={14} className="mr-1"/> New Order</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Orders", value: c.stats?.order_count ?? 0 },
          { label: "Lifetime (grams)", value: c.stats?.lifetime_value_grams ?? 0 },
          { label: "Won deals", value: `₹${(c.stats?.won_deals_value || 0).toLocaleString('en-IN')}` },
          { label: "Last purchase", value: c.stats?.last_purchase_date ? format(new Date(c.stats.last_purchase_date), "dd MMM yyyy") : "—" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-[#E4DFCF] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">{s.label}</div>
            <div className="font-serif text-2xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Preferences (left) */}
        <Card className="lg:col-span-1 border-[#E4DFCF] rounded-xl bg-[#F6EED8]/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Gem size={16} className="text-[#C9A24B]"/><div className="font-serif text-lg">Product Preferences</div></div>
              <Button variant="ghost" size="sm" onClick={() => setEditing(v => !v)} data-testid="edit-preferences-btn"><Edit3 size={14}/></Button>
            </div>
            {!editing ? (
              <div className="space-y-3 text-sm">
                <PrefBlock label="Metals" values={c.preferences?.metals}/>
                <PrefBlock label="Purity" values={c.preferences?.purity}/>
                <PrefBlock label="Categories" values={c.preferences?.categories}/>
                <PrefBlock label="Styles" values={c.preferences?.styles}/>
                <PrefBlock label="Gemstones" values={c.preferences?.gemstones}/>
                {c.preferences?.size_notes && <div><div className="text-[10px] uppercase text-[#4A5A50]">Size notes</div><div>{c.preferences.size_notes}</div></div>}
                {(c.preferences?.budget_min || c.preferences?.budget_max) && <div><div className="text-[10px] uppercase text-[#4A5A50]">Budget</div><div>₹{c.preferences.budget_min?.toLocaleString('en-IN') || 0} - ₹{c.preferences.budget_max?.toLocaleString('en-IN') || 0}</div></div>}
                {c.preferences?.notes && <div><div className="text-[10px] uppercase text-[#4A5A50]">Notes</div><div className="italic text-[#4A5A50]">{c.preferences.notes}</div></div>}
              </div>
            ) : (
              <PrefEditor prefs={c.preferences || {}} onSave={(p) => { savePrefs({ preferences: p }); setEditing(false); }} onCancel={() => setEditing(false)}/>
            )}
          </CardContent>
        </Card>

        {/* Timeline + orders (right) */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs defaultValue="timeline" className="w-full">
            <TabsList className="bg-[#F1EDDF]">
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders">Orders ({c.orders?.length || 0})</TabsTrigger>
              <TabsTrigger value="deals" data-testid="tab-deals">Deals ({c.deals?.length || 0})</TabsTrigger>
              <TabsTrigger value="dates" data-testid="tab-dates">Important Dates</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
                <div className="relative pl-4">
                  <div className="absolute left-1 top-1 bottom-1 w-px bg-[#E4DFCF]"/>
                  {[...(c.interactions || []).map(i => ({...i, kind:"interaction"})), ...(c.orders || []).map(o => ({...o, kind:"order", created_at:o.created_at, summary:`Order ${o.reference_number}`}))]
                    .sort((a,b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((ev, i) => (
                      <div key={ev.id || i} className="relative pb-4">
                        <div className="absolute -left-3 top-1 w-2 h-2 rounded-full bg-[#C9A24B]"/>
                        {ev.kind === "interaction" ? (
                          <>
                            <div className="flex items-center gap-2"><StatusPill status={ev.type}/><div className="text-xs text-[#4A5A50]">{format(new Date(ev.created_at), "dd MMM yyyy, HH:mm")}</div></div>
                            <div className="text-sm mt-1">{ev.summary}</div>
                            <div className="text-xs text-[#4A5A50] mt-0.5">{ev.user_name}</div>
                          </>
                        ) : (
                          <button onClick={() => nav(`/orders/${ev.id}`)} className="text-left">
                            <div className="flex items-center gap-2"><Package size={12} className="text-[#C9A24B]"/><div className="font-serif text-base">{ev.reference_number}</div><StatusPill status={ev.status}/></div>
                            <div className="text-xs text-[#4A5A50] mt-1">{format(new Date(ev.created_at), "dd MMM yyyy")}</div>
                          </button>
                        )}
                      </div>
                    ))}
                  {(!c.interactions?.length && !c.orders?.length) && <div className="text-sm text-[#4A5A50]">No history yet</div>}
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="orders">
              <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
                {(c.orders || []).length === 0 ? <div className="text-sm text-[#4A5A50]">No orders yet</div> : (
                  <div className="divide-y divide-[#E4DFCF]">
                    {c.orders.map(o => (
                      <button key={o.id} onClick={() => nav(`/orders/${o.id}`)} className="w-full flex items-center justify-between py-3 hover:bg-[#F1EDDF] px-2 rounded-lg text-left" data-testid={`contact-order-${o.reference_number}`}>
                        <div>
                          <div className="font-medium">{o.reference_number}</div>
                          <div className="text-xs text-[#4A5A50]">{format(new Date(o.created_at), "dd MMM yyyy")}</div>
                        </div>
                        <div className="flex items-center gap-2"><StatusPill status={o.priority}/><StatusPill status={o.status}/></div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="deals">
              <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
                {(c.deals || []).length === 0 ? <div className="text-sm text-[#4A5A50]">No deals yet</div> : (
                  <div className="space-y-2">
                    {c.deals.map(d => (
                      <div key={d.id} className="flex items-center justify-between py-2 border-b border-[#E4DFCF]">
                        <div>
                          <div className="font-medium">{d.title}</div>
                          <div className="text-xs text-[#4A5A50]">₹{d.estimated_value.toLocaleString('en-IN')}</div>
                        </div>
                        <StatusPill status={d.stage}/>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="dates">
              <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
                {(c.important_dates || []).length === 0 ? <div className="text-sm text-[#4A5A50]">No dates saved</div> : (
                  <div className="space-y-2">
                    {c.important_dates.map((d,i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E4DFCF]">
                        <Cake size={16} className="text-[#C9A24B]"/>
                        <div className="flex-1">
                          <div className="font-medium">{d.label}</div>
                          <div className="text-xs text-[#4A5A50]">{format(new Date(d.date), "dd MMM yyyy")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Interaction modal */}
      <Dialog open={interactionOpen} onOpenChange={setInteractionOpen}>
        <DialogContent data-testid="interaction-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Log interaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={interactionForm.type} onValueChange={(v)=>setInteractionForm({...interactionForm, type:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["call","whatsapp","meeting","note","quote"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Summary</Label><Textarea rows={3} value={interactionForm.summary} onChange={(e)=>setInteractionForm({...interactionForm, summary:e.target.value})} data-testid="interaction-summary"/></div>
            <div><Label>Follow-up date (optional)</Label><Input type="date" value={interactionForm.follow_up_date} onChange={(e)=>setInteractionForm({...interactionForm, follow_up_date:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setInteractionOpen(false)}>Cancel</Button><Button onClick={addInteraction} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="interaction-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal modal */}
      <Dialog open={dealOpen} onOpenChange={setDealOpen}>
        <DialogContent data-testid="deal-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New deal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={dealForm.title} onChange={(e)=>setDealForm({...dealForm, title:e.target.value})} data-testid="deal-title"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stage</Label>
                <Select value={dealForm.stage} onValueChange={(v)=>setDealForm({...dealForm, stage:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["new","contacted","quoted","negotiation","won","lost"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Value (₹)</Label><Input type="number" value={dealForm.estimated_value} onChange={(e)=>setDealForm({...dealForm, estimated_value:e.target.value})}/></div>
            </div>
            <div><Label>Category</Label><Input value={dealForm.product_category} onChange={(e)=>setDealForm({...dealForm, product_category:e.target.value})} placeholder="e.g. Necklace"/></div>
            <div><Label>Expected close</Label><Input type="date" value={dealForm.expected_close_date} onChange={(e)=>setDealForm({...dealForm, expected_close_date:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setDealOpen(false)}>Cancel</Button><Button onClick={addDeal} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="deal-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <WhatsAppWishDialog
        open={wishOpen}
        onOpenChange={setWishOpen}
        contact={c}
        occasion={(c?.important_dates || [])[0] || null}
        companyName={company?.name}
        onSent={load}
      />
      <WhatsAppShareDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        parsed={asParsed()}
      />
      <MergeContactDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        targetId={id}
        targetName={c?.name}
        onDone={() => { setMergeOpen(false); load(); }}
      />
    </div>
  );
}


function MergeContactDialog({ open, onClose, targetId, targetName, onDone }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
  }, [open]);
  React.useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let ignore = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/pick", { params: { q: q.trim() } });
        if (!ignore) setResults((data?.items || []).filter((r) => r.id !== targetId));
      } catch (err) { if (!ignore) setResults([]); }
      finally { if (!ignore) setSearching(false); }
    }, 220);
    return () => { ignore = true; clearTimeout(t); };
  }, [q, targetId]);
  const doMerge = async (source) => {
    if (!window.confirm(`Merge "${source.name}" INTO "${targetName}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/contacts/${targetId}/merge`, { source_id: source.id });
      toast.success(`Merged — ${data.moved.orders} orders, ${data.moved.interactions} interactions, ${data.moved.deals} deals moved`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="merge-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Merge a duplicate into this contact</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[#4A5A50]">
          Find the duplicate contact you want to merge into <b>{targetName}</b>. Its orders,
          interactions, and deals move here. The duplicate is soft-deleted with a merge trail.
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or mobile…"
          className="mt-2"
          data-testid="merge-search-input"
        />
        <div className="max-h-80 overflow-y-auto divide-y divide-[#E4DFCF] mt-2 rounded-lg border border-[#E4DFCF]">
          {searching && <div className="p-3 text-xs text-[#4A5A50]">Searching…</div>}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="p-3 text-xs text-[#4A5A50]">No matches — try a different search.</div>
          )}
          {results.map((r) => (
            <div key={r.id} className="p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-[#0F1E15]">{r.display_name || r.name}</div>
                <div className="text-[11px] text-[#4A5A50]">{r.mobile || "—"}{r.city ? ` · ${r.city}` : ""}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => doMerge(r)}
                data-testid={`merge-into-btn-${r.id}`}
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
              >Merge into this</Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrefBlock({ label, values }) {
  if (!values || values.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase text-[#4A5A50]">{label}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {values.map((v,i) => <span key={i} className="text-xs bg-white border border-[#E4DFCF] text-[#0F1E15] px-2 py-1 rounded-full">{v}</span>)}
      </div>
    </div>
  );
}

function PrefEditor({ prefs, onSave, onCancel }) {
  const [p, setP] = useState(() => ({
    metals: (prefs.metals || []).join(", "),
    purity: (prefs.purity || []).join(", "),
    categories: (prefs.categories || []).join(", "),
    styles: (prefs.styles || []).join(", "),
    gemstones: (prefs.gemstones || []).join(", "),
    size_notes: prefs.size_notes || "",
    budget_min: prefs.budget_min || "",
    budget_max: prefs.budget_max || "",
    notes: prefs.notes || "",
  }));
  const toArr = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  const save = () => onSave({
    metals: toArr(p.metals), purity: toArr(p.purity), categories: toArr(p.categories),
    styles: toArr(p.styles), gemstones: toArr(p.gemstones),
    size_notes: p.size_notes,
    budget_min: p.budget_min === "" ? null : Number(p.budget_min),
    budget_max: p.budget_max === "" ? null : Number(p.budget_max),
    notes: p.notes,
  });
  return (
    <div className="space-y-2 text-sm">
      {[
        ["metals","Metals (comma-separated)"],
        ["purity","Purity"],
        ["categories","Categories"],
        ["styles","Styles"],
        ["gemstones","Gemstones"],
      ].map(([k,l]) => <div key={k}><Label className="text-xs">{l}</Label><Input value={p[k]} onChange={(e)=>setP({...p, [k]:e.target.value})} className="mt-1"/></div>)}
      <div><Label className="text-xs">Size notes</Label><Input value={p.size_notes} onChange={(e)=>setP({...p, size_notes:e.target.value})} className="mt-1"/></div>
      <div className="grid grid-cols-2 gap-2"><div><Label className="text-xs">Budget min</Label><Input type="number" value={p.budget_min} onChange={(e)=>setP({...p, budget_min:e.target.value})} className="mt-1"/></div><div><Label className="text-xs">Budget max</Label><Input type="number" value={p.budget_max} onChange={(e)=>setP({...p, budget_max:e.target.value})} className="mt-1"/></div></div>
      <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={p.notes} onChange={(e)=>setP({...p, notes:e.target.value})} className="mt-1"/></div>
      <div className="flex gap-2 pt-2"><Button onClick={save} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="save-prefs">Save</Button><Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button></div>
    </div>
  );
}
